import DeleteOutlineOutlinedIcon from '@mui/icons-material/DeleteOutlineOutlined';
import Box from '@mui/material/Box';
import IconButton from '@mui/material/IconButton';
import Typography from '@mui/material/Typography';
import { RHFAutoComplete } from '@openx/components-core/lib/AutoComplete/RHFAutoComplete.tsx';
import { RHFNumberField } from '@openx/components-core/lib/NumberField/RHFNumberField.tsx';
import { theme } from '@openx/components-core/lib/theme/theme.ts';
import { FREQUENCY_CAPPING_FIELDS } from '@openx/types/frequencyCapping.ts';
import type { I18nTranslate } from '@openx/types/translations.ts';
import { useCallback, useMemo } from 'react';
import {
  type FieldArrayPath,
  type FieldArrayWithId,
  type FieldPath,
  type FieldValues,
  useFormContext,
  useWatch,
} from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { RHFError } from '../components/RHFError.tsx';
import { buildFieldName, getTimePeriodOptions as defaultGetTimePeriodOptions } from './constants.ts';

export const DEFAULT_FREQUENCY_CAPPING_DATA_TEST_PREFIX = 'frequency-capping';

export const defaultGetDeleteButtonTestId = (index: number): string => `delete-icon-${index}`;

export type FrequencyCappingFieldsProps<TFieldValues extends FieldValues> = {
  name: FieldArrayPath<TFieldValues>;
  fields: FieldArrayWithId<TFieldValues, FieldArrayPath<TFieldValues>, 'id'>[];
  onRemove: (index: number) => void;
  dataTestPrefix?: string;
  getDeleteButtonTestId?: (index: number) => string;
  getTimePeriodOptions?: (t: I18nTranslate) => Record<string, string>;
};

export const FrequencyCappingFields = <TFieldValues extends FieldValues>({
  name,
  fields,
  onRemove,
  dataTestPrefix = DEFAULT_FREQUENCY_CAPPING_DATA_TEST_PREFIX,
  getDeleteButtonTestId = defaultGetDeleteButtonTestId,
  getTimePeriodOptions = defaultGetTimePeriodOptions,
}: FrequencyCappingFieldsProps<TFieldValues>) => {
  const { t } = useTranslation();
  const {
    formState: { errors },
    trigger,
  } = useFormContext<TFieldValues>();
  const timePeriodOptions = useMemo(() => getTimePeriodOptions(t), [getTimePeriodOptions, t]);
  const hasAllowRemove = fields.length > 1;

  const handleFieldBlur = useCallback(async () => {
    if (fields.length > 1) {
      await trigger(name as FieldPath<TFieldValues>);
    }
  }, [fields.length, name, trigger]);

  const watchedNames = useMemo(
    () => fields.map((_, index) => buildFieldName(name, index, FREQUENCY_CAPPING_FIELDS.TIME_PERIOD_UNIT)),
    [fields, name]
  );
  const timePeriodUnits = useWatch<TFieldValues>({
    name: watchedNames as unknown as FieldPath<TFieldValues>[],
  });

  const isOptionDisabled = useCallback(
    (option: string, currentIndex: number) =>
      option === '' ||
      (Array.isArray(timePeriodUnits) &&
        timePeriodUnits.some((unit, index) => index !== currentIndex && unit === option)),
    [timePeriodUnits]
  );

  return (
    <>
      {fields.map((field, index) => (
        <Box
          key={field.id}
          sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 2,
            mb: 3,
            '& .MuiFormControl-root': {
              marginBottom: 0,
            },
          }}
        >
          <RHFNumberField<TFieldValues>
            name={buildFieldName(name, index, FREQUENCY_CAPPING_FIELDS.IMPRESSIONS_LIMIT) as FieldPath<TFieldValues>}
            placeholder={t('Enter a Number e.g. 5')}
            margin="dense"
            data-test={`${dataTestPrefix}-impressions`}
            label={t('Number of Impressions')}
            min={1}
            allowDecimals={false}
            sx={{ maxWidth: 160 }}
          />

          <Typography
            variant="body1"
            sx={{
              lineHeight: { sm: theme.spacing(2) },
              paddingTop: { md: 4, sm: 3 },
              whiteSpace: { md: 'nowrap', sm: 'wrap' },
            }}
            data-test={`${dataTestPrefix}-impressions-per-label`}
          >
            {t('Impressions per')}
          </Typography>

          <RHFNumberField<TFieldValues>
            name={buildFieldName(name, index, FREQUENCY_CAPPING_FIELDS.TIME_PERIOD_VALUE) as FieldPath<TFieldValues>}
            placeholder={t('Enter a number e.g., 5, 10, 20')}
            margin="dense"
            data-test={`${dataTestPrefix}-duration`}
            label={t('Duration')}
            min={1}
            allowDecimals={false}
            onBlur={handleFieldBlur}
            sx={{ maxWidth: 80 }}
          />

          <RHFAutoComplete<string, false, true>
            name={buildFieldName(name, index, FREQUENCY_CAPPING_FIELDS.TIME_PERIOD_UNIT)}
            textFieldProps={{
              label: t('Time Unit'),
              margin: 'dense',
            }}
            disableClearable
            onOptionBlur={handleFieldBlur}
            data-test={`${dataTestPrefix}-unit`}
            renderOptions={{
              dataTest: `${dataTestPrefix}-unit`,
            }}
            fullWidth
            getOptionLabel={(option: string) => timePeriodOptions[option] ?? option}
            getOptionDisabled={(option: string) => isOptionDisabled(option, index)}
            options={Object.keys(timePeriodOptions)}
            sx={{ maxWidth: 160 }}
          />

          {hasAllowRemove ? (
            <Box sx={{ paddingTop: 3, display: 'flex', alignItems: 'flex-start' }}>
              <IconButton onClick={() => onRemove(index)} data-test={getDeleteButtonTestId(index)} size="large">
                <DeleteOutlineOutlinedIcon fontSize="small" />
              </IconButton>
            </Box>
          ) : null}
        </Box>
      ))}

      {/* biome-ignore lint/suspicious/noExplicitAny: RHFError expects a different path type than FieldArrayPath */}
      <RHFError errors={errors} name={name as any} mt={1} />
    </>
  );
};
